import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

/**
 * This component can be used to display progress towards a target/goal.
 *
 * @example
 * <fcom-progress-indicator
 *  [total]="10000"
 *  [progress]="5000"
 *  [progressText]="'Tier points' | finLocalization"
 *  [targetText]="'Gold tier' | finLocalization"
 *  [fillColor]="loyalty-gold-dark-bg">
 *  {{ progress }} / {{ total }}
 * </fcom-progress-indicator>
 */

@Component({
  selector: 'fcom-progress-indicator',
  templateUrl: './progress-indicator.component.html',
  styleUrls: ['./progress-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fillGrow', [
      state(
        'void',
        style({
          transform: 'scaleX(0)',
        })
      ),
      state(
        '*',
        style({
          transform: 'scaleX({{ progress }})',
        }),
        { params: { progress: 0 } }
      ),
      transition('void => *', animate('1500ms cubic-bezier(0.66, 0, 0.33, 1)')),
      transition('* => *', animate('1500ms cubic-bezier(0.66, 0, 0.33, 1)')),
    ]),
  ],
  standalone: false,
})
export class ProgressIndicatorComponent {
  /**
   * The current value on the indicator
   */
  progress = input(0);

  /**
   * The total/target value to achieve
   */
  total = input(100);

  /**
   * Optional descriptive label what type of progress is being indicated
   */
  progressText = input<string>();

  /**
   * Optional descriptive label for the type of target to achieve
   */
  targetText = input<string>();

  /**
   * Progress fill color
   */
  fillColor = input('heather-700-bg surface-secondary');

  /**
   * Descriptive aria-label for progress indicator
   */
  ariaLabel = input.required<string>();

  max = computed(() => this.progress() > this.total());
  percentage = computed(() => (this.max() ? 100 : (this.progress() / this.total()) * 100));
  progressScaleX = computed(() => (this.max() ? 1 : this.percentage() * 0.01));
}
